import { type DatabaseFieldType } from '@databases/types/database';
import { isNonEmptyJiraIssueValue } from '@databases/utils/database/field-types/jiraIssue';
import React, { type FunctionComponent, useMemo, useRef } from 'react';

import { PopoverEdit } from '../PopoverEdit';
import { usePopoverSoftSelection } from '../popover-edit-common/usePopoverSoftSelection';
import { type EditComponentProps } from '../types';

import JiraIssueSelect from './JiraIssueSelect';
import { JiraIssuePopoverValue } from './JiraIssueValue';

const JiraIssueValuePopoverEdit: FunctionComponent<
  EditComponentProps<DatabaseFieldType.JIRA_ISSUE>
> = ({
  field,
  entryId,
  value,
  onChange,
  onFocus,
  onBlur,
  autoFocus,
  isRenderedInSidePanel,
  isBulkEdit,
  ariaLabel,
  initialKey,
}) => {
  usePopoverSoftSelection({
    entryId,
    fieldId: field.id,
    isRenderedInSidePanel,
  });
  const resolvedValues = useMemo(
    () =>
      value.map((v) =>
        isNonEmptyJiraIssueValue(v) ? { ...v, serverId: field.settings.jiraServerId } : v
      ),
    [field.settings.jiraServerId, value]
  );

  const isMulti = field.settings.multi || resolvedValues.length > 1;
  const addDataSectionRef = useRef<HTMLDivElement | null>(null);

  const UserSelectAddDataSection = useMemo(
    () => (
      <div data-testid="jira-issue-add-data-section" ref={addDataSectionRef}>
        <JiraIssueSelect
          cloudId={field.settings.cloudId}
          serverId={field.settings.jiraServerId}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
          autoFocus={autoFocus}
          resolvedValues={resolvedValues}
          isRenderedInSidePanel={isRenderedInSidePanel}
          isMulti={isMulti}
          addDataSectionRef={addDataSectionRef}
          aria-label={ariaLabel}
          initialKey={initialKey}
        />
      </div>
    ),
    [
      field.settings.cloudId,
      field.settings.jiraServerId,
      onChange,
      onFocus,
      onBlur,
      autoFocus,
      resolvedValues,
      isRenderedInSidePanel,
      isMulti,
      ariaLabel,
      initialKey,
    ]
  );

  const CurrentDataSection = useMemo(
    () => (
      <>
        {resolvedValues.map((rValue) => {
          return (
            <JiraIssuePopoverValue
              key={rValue._id}
              resolvedValue={rValue}
              resolvedValues={resolvedValues}
              autoFocus={autoFocus}
              onChange={onChange}
              field={field}
              isLinkPreview
              addDataSectionRef={addDataSectionRef}
            />
          );
        })}
      </>
    ),
    [resolvedValues, onChange, autoFocus, field]
  );

  return (
    <PopoverEdit
      isMultiSelect={isMulti}
      hasCurrentData={value.length > 0}
      currentDataSection={CurrentDataSection}
      addDataSection={UserSelectAddDataSection}
      isRenderedInSidePanel={isRenderedInSidePanel}
      fieldWidth={field.width}
      isBulkEdit={isBulkEdit}
    />
  );
};

export default JiraIssueValuePopoverEdit;
